/**
 * Leviathan - Maintenance Detail Page
 * Quasar Prime LLC
 *
 * View and manage individual maintenance record
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2,
  ArrowLeft,
  Edit,
  Truck,
  Wrench,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Play,
  X,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface MaintenanceRecord {
  id: string;
  truckId: string;
  truckNumber: string;
  truckVin: string | null;
  maintenanceType: string;
  description: string;
  scheduledDate: string;
  completedDate: string | null;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'CANCELLED';
  mileageAtService: number | null;
  nextServiceMileage: number | null;
  nextServiceDate: string | null;
  vendorName: string | null;
  vendorId: string | null;
  laborCost: number;
  partsCost: number;
  totalCost: number;
  invoiceNumber: string | null;
  notes: string | null;
  performedBy: string | null;
  createdByName: string;
  createdAt: string;
}

const MAINTENANCE_TYPE_LABELS: Record<string, string> = {
  OIL_CHANGE: 'Oil Change',
  TIRE_SERVICE: 'Tire Service',
  BRAKE_SERVICE: 'Brake Service',
  DOT_INSPECTION: 'DOT Inspection',
  PUMP_CALIBRATION: 'Pump Calibration',
  METER_CALIBRATION: 'Meter Calibration',
  GENERAL_REPAIR: 'General Repair',
  PREVENTIVE: 'Preventive Maintenance',
  OTHER: 'Other',
};

const STATUS_CONFIG = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    icon: Calendar,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    icon: Clock,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    icon: CheckCircle,
  },
  OVERDUE: {
    label: 'Overdue',
    color: 'bg-red-500/20 text-red-400 border-red-500/50',
    icon: AlertTriangle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
    icon: X,
  },
};

export default function MaintenanceDetail() {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Fetch record details
  const { data: recordData, isLoading } = useQuery({
    queryKey: ['maintenance-record', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/maintenance/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch record');
      return res.json();
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const res = await fetch(`${API_URL}/api/maintenance/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-record', id] });
    },
  });

  const record: MaintenanceRecord | null = recordData?.record || null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="text-center py-12">
        <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">Maintenance record not found</p>
        <Link to="/fleet/maintenance" className="text-amber-400 hover:text-amber-300">
          Back to Maintenance
        </Link>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG.SCHEDULED;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to="/fleet/maintenance"
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">{record.truckNumber}</h1>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded ${statusConfig.color}`}
            >
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
          </div>
          <p className="text-amber-400 text-lg ml-9">
            {MAINTENANCE_TYPE_LABELS[record.maintenanceType] || record.maintenanceType}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {record.status === 'SCHEDULED' && (
            <>
              <Link
                to={`/fleet/maintenance/${id}/edit`}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={() => updateStatusMutation.mutate('IN_PROGRESS')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
              >
                <Play className="w-4 h-4" />
                Start Work
              </button>
            </>
          )}

          {record.status === 'IN_PROGRESS' && (
            <button
              onClick={() => setShowCompleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Complete
            </button>
          )}

          {(record.status === 'SCHEDULED' || record.status === 'IN_PROGRESS') && (
            <button
              onClick={() => updateStatusMutation.mutate('CANCELLED')}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Scheduled" value={formatDate(record.scheduledDate)} />
        <StatCard
          label="Completed"
          value={formatDate(record.completedDate)}
          highlight={record.status === 'COMPLETED'}
        />
        <StatCard
          label="Mileage"
          value={record.mileageAtService ? `${record.mileageAtService.toLocaleString()} mi` : '-'}
        />
        <StatCard label="Total Cost" value={formatCurrency(record.totalCost)} highlight />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Panel title="Service Description">
            <div className="text-slate-300 whitespace-pre-wrap">{record.description}</div>
          </Panel>

          {/* Cost Breakdown */}
          <Panel title="Cost Breakdown">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-2">Labor</div>
                <div className="text-slate-100 text-lg font-semibold">
                  {formatCurrency(record.laborCost)}
                </div>
              </div>
              <div className="text-center p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-2">Parts</div>
                <div className="text-slate-100 text-lg font-semibold">
                  {formatCurrency(record.partsCost)}
                </div>
              </div>
              <div className="text-center p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg">
                <div className="text-amber-400 text-xs uppercase tracking-wider mb-2">Total</div>
                <div className="text-amber-400 text-lg font-bold">
                  {formatCurrency(record.totalCost)}
                </div>
              </div>
            </div>
          </Panel>

          {/* Notes */}
          {record.notes && (
            <Panel title="Notes">
              <div className="text-slate-400 whitespace-pre-wrap">{record.notes}</div>
            </Panel>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Truck Info */}
          <Panel title="Truck">
            <div className="space-y-3">
              <Link
                to={`/trucks/${record.truckId}`}
                className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold"
              >
                <Truck className="w-4 h-4" />
                {record.truckNumber}
              </Link>
              {record.truckVin && (
                <InfoRow label="VIN" value={record.truckVin} />
              )}
            </div>
          </Panel>

          {/* Next Service */}
          {(record.nextServiceMileage || record.nextServiceDate) && (
            <Panel title="Next Service Due">
              <div className="space-y-3">
                {record.nextServiceMileage && (
                  <InfoRow
                    label="At Mileage"
                    value={`${record.nextServiceMileage.toLocaleString()} mi`}
                  />
                )}
                {record.nextServiceDate && (
                  <InfoRow label="By Date" value={formatDate(record.nextServiceDate)} />
                )}
              </div>
            </Panel>
          )}

          {/* Vendor */}
          {(record.vendorName || record.invoiceNumber) && (
            <Panel title="Vendor">
              <div className="space-y-3">
                {record.vendorName && <InfoRow label="Vendor" value={record.vendorName} />}
                {record.invoiceNumber && (
                  <InfoRow label="Invoice #" value={record.invoiceNumber} />
                )}
              </div>
            </Panel>
          )}

          {/* Performed By */}
          {record.performedBy && (
            <Panel title="Performed By">
              <div className="text-slate-100">{record.performedBy}</div>
            </Panel>
          )}

          {/* Metadata */}
          <Panel title="Details">
            <div className="space-y-3">
              <InfoRow label="Created" value={formatDate(record.createdAt)} />
              <InfoRow label="Created By" value={record.createdByName} />
              <InfoRow label="Record ID" value={record.id.slice(0, 8)} />
            </div>
          </Panel>
        </div>
      </div>

      {/* Complete Modal */}
      {showCompleteModal && accessToken && (
        <CompleteMaintenanceModal
          recordId={id!}
          accessToken={accessToken}
          onClose={() => setShowCompleteModal(false)}
          onSuccess={() => {
            setShowCompleteModal(false);
            queryClient.invalidateQueries({ queryKey: ['maintenance-record', id] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`bg-slate-800/50 border rounded-xl p-4 ${
        highlight ? 'border-amber-500/50' : 'border-slate-700/50'
      }`}
    >
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-xl font-bold ${highlight ? 'text-amber-400' : 'text-slate-100'}`}>
        {value}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-slate-100">{value}</div>
    </div>
  );
}

function CompleteMaintenanceModal({
  recordId,
  accessToken,
  onClose,
  onSuccess,
}: {
  recordId: string;
  accessToken: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    completedDate: new Date().toISOString().split('T')[0],
    mileageAtService: '',
    laborCost: '',
    partsCost: '',
    nextServiceMileage: '',
    nextServiceDate: '',
    notes: '',
    performedBy: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/maintenance/${recordId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          completedDate: formData.completedDate,
          mileageAtService: formData.mileageAtService
            ? parseInt(formData.mileageAtService)
            : undefined,
          laborCost: formData.laborCost ? parseFloat(formData.laborCost) : undefined,
          partsCost: formData.partsCost ? parseFloat(formData.partsCost) : undefined,
          nextServiceMileage: formData.nextServiceMileage
            ? parseInt(formData.nextServiceMileage)
            : undefined,
          nextServiceDate: formData.nextServiceDate || undefined,
          notes: formData.notes || undefined,
          performedBy: formData.performedBy || undefined,
        }),
      });

      if (response.ok) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to complete maintenance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalCost =
    (parseFloat(formData.laborCost) || 0) + (parseFloat(formData.partsCost) || 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-100">Complete Maintenance</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Completed Date *</label>
              <input
                type="date"
                value={formData.completedDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, completedDate: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Mileage</label>
              <input
                type="number"
                value={formData.mileageAtService}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mileageAtService: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Current mileage"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Labor Cost</label>
              <input
                type="number"
                value={formData.laborCost}
                onChange={(e) => setFormData((prev) => ({ ...prev, laborCost: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.01"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Parts Cost</label>
              <input
                type="number"
                value={formData.partsCost}
                onChange={(e) => setFormData((prev) => ({ ...prev, partsCost: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="text-right text-amber-400 font-semibold">
            Total: ${totalCost.toFixed(2)}
          </div>

          <div className="border-t border-slate-700 pt-4">
            <div className="text-slate-500 text-xs uppercase tracking-wider mb-4">
              Next Service
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">At Mileage</label>
                <input
                  type="number"
                  value={formData.nextServiceMileage}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, nextServiceMileage: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">By Date</label>
                <input
                  type="date"
                  value={formData.nextServiceDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, nextServiceDate: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Performed By</label>
            <input
              type="text"
              value={formData.performedBy}
              onChange={(e) => setFormData((prev) => ({ ...prev, performedBy: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              placeholder="Technician name"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Complete Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
